import { Fragment, useCallback, useEffect, useState } from 'react';
import {
  makeStyles,
  tokens,
  Text,
  Button,
  Table,
  TableHeader,
  TableRow,
  TableHeaderCell,
  TableBody,
  TableCell,
  Spinner,
} from '@fluentui/react-components';
import {
  Card,
} from '@fluentui/react-components';
import { StatusPill } from '../common/StatusPill';
import { PlayRegular, ChevronLeftFilled, ChevronRightFilled } from '@fluentui/react-icons';
import { PageHeader } from '../common/PageHeader';
import { ViewAsAction } from '../common/ViewAsAction';
import { NativeDateInput } from '../common/NativeDateInput';
import { LoadingState } from '../common/LoadingState';
import { ErrorState } from '../common/ErrorState';
import { EmptyState } from '../common/EmptyState';
import { api } from '../../services/api';
import type { RecordingListItem, RecordingListResponse } from '../../types';

const useStyles = makeStyles({
  content: {
    padding: tokens.spacingHorizontalL,
    maxWidth: '1400px',
    margin: '0 auto',
  },
  filters: {
    display: 'flex',
    gap: tokens.spacingHorizontalM,
    marginBottom: tokens.spacingVerticalL,
    flexWrap: 'wrap',
    alignItems: 'flex-end',
  },
  playerCard: {
    marginTop: tokens.spacingVerticalS,
    marginBottom: tokens.spacingVerticalM,
    padding: tokens.spacingHorizontalM,
    backgroundColor: tokens.colorNeutralBackground2,
  },
  playerHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: tokens.spacingVerticalS,
  },
  audio: {
    width: '100%',
  },
  pagination: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    marginTop: tokens.spacingVerticalL,
  },
  errorBanner: {
    marginBottom: tokens.spacingVerticalM,
  },
});

function formatDuration(seconds: number | null): string {
  if (seconds == null) return '—';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

function formatDateTime(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function displayName(item: RecordingListItem, side: 'caller' | 'callee'): string {
  const name = side === 'caller' ? item.caller_display_name : item.callee_display_name;
  const num = side === 'caller' ? item.caller_number : item.callee_number;
  return name || num || '—';
}

interface RecordingsListProps {
  emulationPanelOpen: boolean;
  onToggleEmulationPanel: () => void;
}

export function RecordingsList({ emulationPanelOpen, onToggleEmulationPanel }: RecordingsListProps) {
  const styles = useStyles();
  const [data, setData] = useState<RecordingListResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [playingId, setPlayingId] = useState<number | null>(null);
  const [playingUrl, setPlayingUrl] = useState<string | null>(null);
  const [playLoading, setPlayLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.getRecordings({
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
        page,
        page_size: 25,
      });
      setData(res);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load recordings');
    } finally {
      setLoading(false);
    }
  }, [page, dateFrom, dateTo]);

  useEffect(() => {
    load();
  }, [load]);

  const handlePlay = async (recordingId: number) => {
    if (playingId === recordingId) {
      setPlayingId(null);
      setPlayingUrl(null);
      return;
    }
    setPlayLoading(true);
    setPlayingId(recordingId);
    setPlayingUrl(null);
    try {
      const res = await api.getRecordingDownloadUrl(recordingId);
      setPlayingUrl(res.download_url);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to get recording URL');
      setPlayingId(null);
    } finally {
      setPlayLoading(false);
    }
  };

  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.page_size)) : 1;

  return (
    <div>
      <PageHeader
        title="Call Recordings"
        subtitle={data ? `${data.total} recording${data.total === 1 ? '' : 's'}` : undefined}
        breadcrumb="Recordings"
        actions={
          <ViewAsAction
            emulationPanelOpen={emulationPanelOpen}
            onToggleEmulationPanel={onToggleEmulationPanel}
          />
        }
      />

      <div className={styles.content}>
      <div className={styles.filters}>
        <div>
          <Text size={200} block>From</Text>
          <NativeDateInput
            value={dateFrom}
            onChange={(v) => {
              setDateFrom(v);
              setPage(1);
            }}
          />
        </div>
        <div>
          <Text size={200} block>To</Text>
          <NativeDateInput
            value={dateTo}
            onChange={(v) => {
              setDateTo(v);
              setPage(1);
            }}
          />
        </div>
        <Button
          onClick={() => {
            setDateFrom('');
            setDateTo('');
            setPage(1);
          }}
        >
          Clear
        </Button>
      </div>

      {error && (
        <ErrorState
          className={styles.errorBanner}
          message={error}
          onRetry={() => load()}
        />
      )}

      {loading && !data ? (
        <LoadingState variant="panel" height={240} />
      ) : !data || data.items.length === 0 ? (
        <EmptyState
          title="No recordings yet"
          hint="Place a compliance-recorded Teams call to see one here."
        />
      ) : (
        <>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHeaderCell>Start Time</TableHeaderCell>
                <TableHeaderCell>Caller</TableHeaderCell>
                <TableHeaderCell>Callee</TableHeaderCell>
                <TableHeaderCell>Duration</TableHeaderCell>
                <TableHeaderCell>Status</TableHeaderCell>
                <TableHeaderCell>Playback</TableHeaderCell>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.items.map((item) => (
                <Fragment key={item.id}>
                  <TableRow>
                    <TableCell>{formatDateTime(item.call_start_time)}</TableCell>
                    <TableCell>{displayName(item, 'caller')}</TableCell>
                    <TableCell>{displayName(item, 'callee')}</TableCell>
                    <TableCell>{formatDuration(item.duration_seconds)}</TableCell>
                    <TableCell>
                      <StatusPill tone={item.recording_status === 'completed' ? 'positive' : 'warning'}>
                        {item.recording_status}
                      </StatusPill>
                    </TableCell>
                    <TableCell>
                      <Button
                        size="small"
                        icon={<PlayRegular />}
                        onClick={() => handlePlay(item.id)}
                        disabled={item.recording_status !== 'completed'}
                      >
                        {playingId === item.id ? 'Hide' : 'Play'}
                      </Button>
                    </TableCell>
                  </TableRow>
                  {playingId === item.id && (
                    <TableRow>
                      <TableCell colSpan={6}>
                        <Card className={styles.playerCard}>
                          <div className={styles.playerHeader}>
                            <Text weight="semibold">
                              {displayName(item, 'caller')} ↔ {displayName(item, 'callee')}
                            </Text>
                            <Text size={200}>Recording ID: {item.recording_id}</Text>
                          </div>
                          {playLoading ? (
                            <Spinner size="small" label="Fetching audio..." />
                          ) : playingUrl ? (
                            <audio className={styles.audio} controls src={playingUrl} autoPlay />
                          ) : (
                            <Text>No audio URL available</Text>
                          )}
                        </Card>
                      </TableCell>
                    </TableRow>
                  )}
                </Fragment>
              ))}
            </TableBody>
          </Table>

          <div className={styles.pagination}>
            <Button
              icon={<ChevronLeftFilled />}
              disabled={page <= 1 || loading}
              onClick={() => setPage((p) => p - 1)}
            >
              Previous
            </Button>
            <Text>
              Page {page} of {totalPages}
            </Text>
            <Button
              icon={<ChevronRightFilled />}
              iconPosition="after"
              disabled={page >= totalPages || loading}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </Button>
          </div>
        </>
      )}
      </div>
    </div>
  );
}
